import { useState, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageCircle, Search, User, Mail, Clock, Calendar, ChevronRight, Eye, Shield, BookOpen, Plus, AlertTriangle, CheckCircle, XCircle, ThumbsUp, ThumbsDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get country flag emoji
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode === 'XX' || countryCode === 'LC') return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

export default function ChatModeration() {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [globalRateLimitEnabled, setGlobalRateLimitEnabled] = useState(false);
  
  // Error Logs State
  const [chatErrors, setChatErrors] = useState([]);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  
  // Coaching Notes State
  const [coachingNoteDialogOpen, setCoachingNoteDialogOpen] = useState(false);
  const [coachingNoteCategory, setCoachingNoteCategory] = useState("");
  const [coachingNoteContent, setCoachingNoteContent] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);
  
  // Admin Feedback State
  const [submittingFeedback, setSubmittingFeedback] = useState({});

  useEffect(() => {
    fetchConversations();
    fetchGlobalRateLimit();
    fetchExistingCategories();
    fetchChatErrors();
  }, []);

  useEffect(() => {
    // Filter conversations based on search query
    if (searchQuery.trim() === "") {
      setFilteredConversations(conversations);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = conversations.filter(
        (conv) =>
          conv.user_name.toLowerCase().includes(query) ||
          conv.user_email.toLowerCase().includes(query) ||
          conv.actor_name.toLowerCase().includes(query)
      );
      setFilteredConversations(filtered);
    }
  }, [searchQuery, conversations]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/chat/all-conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations);
      setFilteredConversations(response.data.conversations);
    } catch (error) {
      toast.error("Failed to fetch conversations");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalRateLimit = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/settings/ip-rate-limit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalRateLimitEnabled(response.data.global_ip_rate_limit_enabled || false);
    } catch (error) {
      console.error("Failed to fetch rate limit settings", error);
    }
  };

  const fetchExistingCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/coaching-notes/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingCategories(response.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch coaching categories", error);
    }
  };

  const updateGlobalRateLimit = async (enabled) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/settings/ip-rate-limit?enabled=${enabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGlobalRateLimitEnabled(enabled);
      toast.success(`Global IP rate limiting ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to update rate limiting");
      console.error(error);
    }
  };

  const fetchChatErrors = async () => {
    try {
      setLoadingErrors(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/chat-errors?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatErrors(response.data.errors || []);
    } catch (error) {
      console.error("Failed to fetch chat errors", error);
    } finally {
      setLoadingErrors(false);
    }
  };

  const resolveError = async (errorId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/admin/chat-errors/${errorId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Error marked as resolved");
      fetchChatErrors(); // Refresh the list
    } catch (error) {
      toast.error("Failed to resolve error");
      console.error(error);
    }
  };

  const viewConversation = (conversation) => {
    setSelectedConversation(conversation);
    setDialogOpen(true);
  };

  const viewErrorDetails = (error) => {
    setSelectedError(error);
    setErrorDialogOpen(true);
  };

  const openCoachingNoteDialog = () => {
    setCoachingNoteContent("");
    setCoachingNoteCategory("");
    setCoachingNoteDialogOpen(true);
  };

  const submitCoachingNote = async () => {
    if (!coachingNoteContent.trim()) {
      toast.error("Please enter a coaching note");
      return;
    }

    if (!coachingNoteCategory.trim()) {
      toast.error("Please enter a category");
      return;
    }

    setSubmittingNote(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-actors/${selectedConversation.actor_id}/coaching-notes`,
        {
          category: coachingNoteCategory,
          content: coachingNoteContent,
          conversation_id: selectedConversation.session_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Coaching note added! AI Actor will learn from this feedback.");
      setCoachingNoteDialogOpen(false);
      setCoachingNoteContent("");
      setCoachingNoteCategory("");
      fetchExistingCategories(); // Refresh categories list
    } catch (error) {
      toast.error("Failed to add coaching note");
      console.error(error);
    } finally {
      setSubmittingNote(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const formatRelativeTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };
  
  const handleAdminFeedback = async (message, feedbackType) => {
    const messageKey = message.id || message.timestamp;
    
    // Check if message has required fields
    if (!message.id) {
      toast.error("Cannot submit feedback: Message ID is missing");
      console.error("Message object:", message);
      return;
    }
    
    if (!selectedConversation.session_id) {
      toast.error("Cannot submit feedback: Session ID is missing");
      return;
    }
    
    if (!selectedConversation.actor_id) {
      toast.error("Cannot submit feedback: Actor ID is missing");
      return;
    }
    
    try {
      setSubmittingFeedback(prev => ({...prev, [messageKey]: feedbackType}));
      
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/public/chat/feedback`,
        {
          session_id: selectedConversation.session_id,
          message_id: message.id,
          actor_id: selectedConversation.actor_id,
          feedback_type: feedbackType,
          feedback_comment: `Admin review: ${feedbackType === 'positive' ? 'Good response' : 'Needs improvement'}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Admin feedback submitted: ${feedbackType === 'positive' ? 'Thumbs Up' : 'Thumbs Down'}`);
      
      // Keep the feedback state for visual indication
      setTimeout(() => {
        setSubmittingFeedback(prev => {
          const newState = {...prev};
          delete newState[messageKey];
          return newState;
        });
      }, 2000);
      
    } catch (error) {
      console.error("Feedback submission error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Request payload:", {
        session_id: selectedConversation.session_id,
        message_id: message.id,
        actor_id: selectedConversation.actor_id,
        feedback_type: feedbackType
      });
      
      const errorMsg = error.response?.data?.detail || "Failed to submit feedback";
      toast.error(errorMsg);
      
      setSubmittingFeedback(prev => {
        const newState = {...prev};
        delete newState[messageKey];
        return newState;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <MessageCircle className="w-8 h-8 text-indigo-600" />
          Chat Moderation
        </h1>
        <p className="text-muted-foreground mt-2">
          View and monitor all conversations between users and AI Actors
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Conversations</p>
                <p className="text-2xl font-bold">{conversations.length}</p>
              </div>
              <MessageCircle className="w-8 h-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Messages</p>
                <p className="text-2xl font-bold">
                  {conversations.reduce((sum, conv) => sum + conv.message_count, 0)}
                </p>
              </div>
              <Mail className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unique Users</p>
                <p className="text-2xl font-bold">
                  {new Set(conversations.map(c => c.user_email)).size}
                </p>
              </div>
              <User className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
        <Input
          placeholder="Search by user name, email, or AI Actor..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* IP Rate Limiting Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            Global IP Rate Limiting
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            When enabled, limits each IP address to a total of 10 messages across ALL AI Actors combined.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors">
            <div className="flex-1">
              <div className="font-medium text-slate-900">IP Rate Limiting</div>
              <div className="text-sm text-muted-foreground mt-1">
                Limit: <span className="font-semibold text-indigo-600">10 total messages per IP</span> across all actors
              </div>
              {globalRateLimitEnabled && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 mt-2">
                  Active - Users limited to 10 messages total
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="global-rate-limit" className="text-sm font-medium">
                {globalRateLimitEnabled ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="global-rate-limit"
                checked={globalRateLimitEnabled}
                onCheckedChange={(checked) => updateGlobalRateLimit(checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chat Error Logs */}
      <Card className="border-2 border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Chat Error Logs
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Failed chat attempts where both primary and backup models failed
          </p>
        </CardHeader>
        <CardContent>
          {loadingErrors ? (
            <p className="text-sm text-muted-foreground text-center py-4">Loading errors...</p>
          ) : chatErrors.length === 0 ? (
            <div className="p-4 border rounded-lg bg-green-50 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm text-green-700 font-medium">No errors logged! 🎉</p>
              <p className="text-xs text-muted-foreground mt-1">All chats are working smoothly</p>
            </div>
          ) : (
            <div className="space-y-2">
              {chatErrors.slice(0, 10).map((error) => (
                <div 
                  key={error.id} 
                  className={`flex items-start justify-between p-3 border rounded-lg ${error.resolved ? 'bg-gray-50 opacity-60' : 'bg-red-50'}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {error.resolved ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className="font-semibold text-sm">{error.actor_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {error.error_type}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatDate(error.timestamp)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      User: {error.user_name} ({error.user_email})
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Primary: {error.primary_model} | Backup: {error.backup_model || 'None'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewErrorDetails(error)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Details
                    </Button>
                    {!error.resolved && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => resolveError(error.id)}
                        className="text-green-600 hover:text-green-700"
                      >
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {chatErrors.length > 10 && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Showing 10 of {chatErrors.length} errors
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversations Table */}
      {filteredConversations.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-muted-foreground">
              {searchQuery ? "No conversations found" : "No conversations yet"}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {searchQuery ? "Try adjusting your search" : "Conversations will appear here once users start chatting with AI Actors"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      AI Actor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Messages/Duration
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                      User Feedback
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Admin Feedback
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Started
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredConversations.map((conversation) => (
                    <tr key={conversation.session_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                            {conversation.actor_name}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge 
                          variant="outline" 
                          className={conversation.type === 'call' 
                            ? 'bg-green-50 text-green-700 border-green-200' 
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                          }
                        >
                          {conversation.type === 'call' ? '📞 Call' : '💬 Chat'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-900">
                            {conversation.user_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {conversation.user_email}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-slate-600">
                          {conversation.ip_address && conversation.ip_address !== 'Unknown (Legacy)' 
                            ? conversation.ip_address 
                            : <span className="text-slate-400 italic">Not tracked</span>
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getCountryFlag(conversation.country_code)}</span>
                          <div>
                            <div className="text-sm font-medium text-slate-900">
                              {conversation.country && conversation.country !== 'Unknown' 
                                ? conversation.country 
                                : <span className="text-slate-400 italic">Not tracked</span>
                              }
                            </div>
                            {conversation.city && conversation.city !== '' && (
                              <div className="text-xs text-slate-500">
                                {conversation.city}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <Badge variant="secondary" className="font-semibold">
                            {conversation.message_count} msgs
                          </Badge>
                          {conversation.type === 'call' && conversation.duration && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              ⏱️ {conversation.duration}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-3">
                          <div className="flex items-center gap-1">
                            <ThumbsUp className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-700">
                              {conversation.feedback?.user_positive || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ThumbsDown className="w-4 h-4 text-red-600" />
                            <span className="text-sm font-medium text-red-700">
                              {conversation.feedback?.user_negative || 0}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-3">
                          <div className="flex items-center gap-1">
                            <ThumbsUp className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-700">
                              {conversation.feedback?.admin_positive || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ThumbsDown className="w-4 h-4 text-orange-600" />
                            <span className="text-sm font-medium text-orange-700">
                              {conversation.feedback?.admin_negative || 0}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">
                          {formatDate(conversation.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">
                          {formatRelativeTime(conversation.last_message_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewConversation(conversation)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conversation Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Conversation Details</DialogTitle>
          </DialogHeader>

          {selectedConversation && (
            <div className="space-y-6 mt-4">
              {/* Conversation Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <Badge 
                    variant="outline" 
                    className={selectedConversation.type === 'call' 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                    }
                  >
                    {selectedConversation.type === 'call' ? '📞 Call' : '💬 Chat'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">AI Actor</p>
                  <p className="font-medium">{selectedConversation.actor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">User</p>
                  <p className="font-medium">{selectedConversation.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedConversation.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Messages</p>
                  <p className="font-medium">{selectedConversation.message_count}</p>
                </div>
                {selectedConversation.type === 'call' && selectedConversation.duration && (
                  <div>
                    <p className="text-sm text-muted-foreground">Duration</p>
                    <p className="font-medium">⏱️ {selectedConversation.duration}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Started</p>
                  <p className="font-medium text-sm">{formatDate(selectedConversation.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Message</p>
                  <p className="font-medium text-sm">{formatDate(selectedConversation.last_message_at)}</p>
                </div>
              </div>

              {/* Add Coaching Note Button */}
              <div className="flex justify-end">
                <Button
                  onClick={openCoachingNoteDialog}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Add Coaching Note
                </Button>
              </div>

              {/* Messages */}
              <div>
                <h3 className="font-semibold mb-4">
                  {selectedConversation.type === 'call' ? 'Call Transcript' : 'Chat History'}
                </h3>
                <div className="space-y-4 max-h-96 overflow-y-auto border rounded-lg p-4 bg-slate-50">
                  {selectedConversation.messages.map((msg, index) => (
                    <div key={msg.id || index} className="space-y-3">
                      {/* User Message */}
                      {msg.user_message && (
                        <div className="flex justify-end">
                          <div className="max-w-[70%] rounded-lg px-4 py-2 bg-slate-900 text-white">
                            <p className="text-xs text-slate-300 mb-1">
                              {selectedConversation.user_name}
                            </p>
                            <div className="text-sm prose prose-sm prose-invert max-w-none">
                              <ReactMarkdown>{msg.user_message}</ReactMarkdown>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* AI Assistant Response */}
                      {msg.assistant_message && (
                        <div className="flex justify-start">
                          <div className="max-w-[70%] rounded-lg px-4 py-2 bg-white border border-slate-200">
                            <p className="text-xs text-muted-foreground mb-1">
                              {selectedConversation.actor_name}
                            </p>
                            <div className="text-sm prose prose-sm max-w-none">
                              <ReactMarkdown>{msg.assistant_message}</ReactMarkdown>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-xs text-muted-foreground">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </p>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-7 px-2 ${submittingFeedback[msg.id || msg.timestamp] === 'positive' ? 'text-green-600' : 'text-gray-500 hover:text-green-600'}`}
                                  onClick={() => handleAdminFeedback(msg, 'positive')}
                                  disabled={!!submittingFeedback[msg.id || msg.timestamp]}
                                >
                                  <ThumbsUp className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-7 px-2 ${submittingFeedback[msg.id || msg.timestamp] === 'negative' ? 'text-red-600' : 'text-gray-500 hover:text-red-600'}`}
                                  onClick={() => handleAdminFeedback(msg, 'negative')}
                                  disabled={!!submittingFeedback[msg.id || msg.timestamp]}
                                >
                                  <ThumbsDown className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Session Info */}
              <div className="text-xs text-muted-foreground border-t pt-4">
                <p>Session ID: {selectedConversation.session_id}</p>
                <p>Actor ID: {selectedConversation.actor_id}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Coaching Note Dialog */}
      <Dialog open={coachingNoteDialogOpen} onOpenChange={setCoachingNoteDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              Add Coaching Note to AI Actor
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-indigo-900">
                <strong>💡 Tip:</strong> Based on this conversation, provide guidance to improve the AI Actor's future responses. 
                These notes will be automatically included in the AI's system prompt for all future chats.
              </p>
            </div>

            {/* Category Input with Suggestions */}
            <div>
              <Label>Category (type your own or select from existing)</Label>
              <Input
                value={coachingNoteCategory}
                onChange={(e) => setCoachingNoteCategory(e.target.value)}
                placeholder="e.g., Pricing, Communication, Sales Strategy, Client Handling..."
                list="category-suggestions"
                className="mt-1"
              />
              <datalist id="category-suggestions">
                {existingCategories.map((cat, index) => (
                  <option key={index} value={cat} />
                ))}
              </datalist>
              <p className="text-xs text-muted-foreground mt-1">
                {existingCategories.length > 0 
                  ? `💡 Existing categories: ${existingCategories.join(', ')}` 
                  : "Type any category name - it will be created automatically"}
              </p>
            </div>

            {/* Coaching Note Content */}
            <div>
              <Label>Coaching Note</Label>
              <Textarea
                value={coachingNoteContent}
                onChange={(e) => setCoachingNoteContent(e.target.value)}
                placeholder="Example: Always mention the hourly rate of $150 upfront when asked about pricing. Never agree to rates below $100/hour."
                rows={5}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Be specific and actionable. The AI will follow these guidelines in all future conversations.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setCoachingNoteDialogOpen(false)}
                disabled={submittingNote}
              >
                Cancel
              </Button>
              <Button
                onClick={submitCoachingNote}
                disabled={submittingNote || !coachingNoteContent.trim() || !coachingNoteCategory.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {submittingNote ? "Adding..." : "Add Coaching Note"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Details Dialog */}
      <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Chat Error Details
            </DialogTitle>
          </DialogHeader>

          {selectedError && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">AI Actor</p>
                  <p className="font-semibold">{selectedError.actor_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Error Type</p>
                  <Badge variant="destructive">{selectedError.error_type}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">User</p>
                  <p className="text-sm">{selectedError.user_name} ({selectedError.user_email})</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Timestamp</p>
                  <p className="text-sm">{formatDate(selectedError.timestamp)}</p>
                </div>
              </div>

              {/* User Message */}
              <div>
                <p className="text-sm font-semibold mb-2">User's Message:</p>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm">{selectedError.user_message}</p>
                </div>
              </div>

              {/* Primary Model Error */}
              <div>
                <p className="text-sm font-semibold mb-2">Primary Model: {selectedError.primary_model}</p>
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-xs font-mono whitespace-pre-wrap">{selectedError.primary_error}</p>
                </div>
              </div>

              {/* Backup Model Error */}
              {selectedError.backup_model && (
                <div>
                  <p className="text-sm font-semibold mb-2">Backup Model: {selectedError.backup_model}</p>
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                    {selectedError.backup_error ? (
                      <p className="text-xs font-mono whitespace-pre-wrap">{selectedError.backup_error}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">No backup error (backup not attempted or succeeded)</p>
                    )}
                  </div>
                </div>
              )}

              {!selectedError.backup_model && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">⚠️ No backup model configured for this AI Actor</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setErrorDialogOpen(false)}
                >
                  Close
                </Button>
                {!selectedError.resolved && (
                  <Button
                    onClick={() => {
                      resolveError(selectedError.id);
                      setErrorDialogOpen(false);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Mark as Resolved
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
